import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeftIcon, LinkedinIcon, MailIcon } from "lucide-react";
import { useHackathon } from "../contexts/HackathonContext";
import { Avatar } from "../components/Avatar";
import { subjectForGroup } from "../utils/subjects";
import { formatDay } from "../utils/dates";
export function PersonProfile() {
  const {
    id
  } = useParams();
  const {
    state
  } = useHackathon();
  const student = state.students.find(s => s.id === id);
  const mentor = state.mentors.find(m => m.id === id);
  const person = student ?? mentor;
  if (!person) {
    return <main className="mx-auto max-w-6xl px-5 py-16 text-center md:px-8" data-id="element-935" data-mp="386qjbrwfotyw">
        <p className="text-lg font-semibold" data-id="element-936" data-mp="33748fp42vpt9">Profile not found</p>
        <Link to="/organizer/people" className="mt-2 inline-block text-sm text-muted underline" data-id="element-937" data-mp="334hrwzz7lzyw">
          Back to people
        </Link>
      </main>;
  }
  const groupIds = student ? student.groupId ? [student.groupId] : [] : mentor!.groupIds;
  const subtitle = student ? student.school === "link" ? "Link School of Business · Brazil" : "Mahindra University · India" : [mentor!.organization, mentor!.expertise].filter(Boolean).join(" · ") || "Mentor";
  const teammates = student ? state.students.filter(s => s.groupId === student.groupId && s.id !== student.id && student.groupId) : [];
  const submissions = student ? state.videos.filter(v => v.submittedBy === student.id).sort((a, b) => a.day - b.day) : [];
  return <main className="mx-auto max-w-4xl px-5 py-10 md:px-8 md:py-12" data-id="element-938" data-mp="xngdou6rofsd">
      <Link to="/organizer/people" className="inline-flex items-center gap-1.5 text-sm text-muted transition-colors duration-150 hover:text-ink" data-id="element-939" data-mp="jp04mwlfmd72">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-940" data-mp="noqr4o3xe75s" />
        All people
      </Link>

      <header className="mt-6 flex flex-col gap-6 sm:flex-row sm:items-center" data-id="element-941" data-mp="34de4edn8p4tc">
        <Avatar name={person.name} school={student ? student.school : "mentor"} photo={person.photo} size="xl" data-id="element-942" data-mp="38hlv2gd9cuu7" />
        <div className="min-w-0 flex-1" data-id="element-943" data-mp="1ewimyr61z55u">
          <p className="text-sm font-semibold text-muted" data-id="element-944" data-mp="1ezoe0by8sh47">{student ? "Student" : "Mentor"}</p>
          <h1 className="text-3xl font-semibold tracking-tight" data-id="element-945" data-mp="1a13ao3np1hbj">{person.name}</h1>
          <p className="mt-1 text-[15px] text-muted" data-id="element-946" data-mp="1567jf5qa6xzh">{subtitle}</p>
          <div className="mt-3 flex flex-wrap gap-3 text-sm" data-id="element-947" data-mp="107w8sss5wjg7">
            {person.email && <a href={`mailto:${person.email}`} className="inline-flex items-center gap-1.5 hover:underline" data-id="element-948" data-mp="w8dljqcvbwa7">
                <MailIcon className="h-4 w-4 text-muted" aria-hidden="true" data-id="element-949" data-mp="r7g6vnr2hkdg" />
                {person.email}
              </a>}
            {person.linkedin && <a href={person.linkedin.startsWith("http") ? person.linkedin : `https://${person.linkedin}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:underline" data-id="element-950" data-mp="hauzivrkrx6g">
                <LinkedinIcon className="h-4 w-4 text-muted" aria-hidden="true" data-id="element-951" data-mp="2k5czsskbin9y" />
                LinkedIn
              </a>}
          </div>
        </div>

      </header>

      {!person.joinedAt && <p className="mt-6 rounded-xl bg-bryellow/15 px-4 py-3 text-sm" data-id="element-952" data-mp="2swwxdbd7yr3c">
          This person hasn't completed their profile yet.
        </p>}

      <div className="mt-10 grid gap-10 md:grid-cols-[1fr_260px]" data-id="element-953" data-mp="2xym7mosmb0qd">
        <section aria-labelledby="about-heading" data-id="element-954" data-mp="217ag8tajhijj">
          <h2 id="about-heading" className="pb-2 text-lg font-semibold" data-id="element-955" data-mp="257uey1argu7s">
            About
          </h2>
          <p className="whitespace-pre-line text-[15px] leading-relaxed" data-id="element-956" data-mp="2a6xcl0oevy5q">
            {person.bio || <span className="text-muted" data-id="element-957" data-mp="2a99xnmpguon0">No bio yet.</span>}
          </p>
          {student && (student.role || student.major) && <dl className="mt-6 grid grid-cols-2 gap-4 border-t border-line pt-4 text-sm" data-id="element-958" data-mp="3lmvmt7vbsq8j">
              <div data-id="element-959" data-mp="3qknouupopsqg">
                <dt className="text-muted" data-id="element-960" data-mp="3qk44z8gh4u9s">Role in team</dt>
                <dd className="font-medium" data-id="element-961" data-mp="13cg9icq7o8mq">{student.role || "—"}</dd>
              </div>
              <div data-id="element-962" data-mp="ydmugfva09kg">
                <dt className="text-muted" data-id="element-963" data-mp="ye6crxou8zz0">Program / major</dt>
                <dd className="font-medium" data-id="element-964" data-mp="yfpwxzdm7v62">{student.major || "—"}</dd>
              </div>
            </dl>}
          {student && <div className="mt-8" data-id="element-965" data-mp="17e1u6k8zlr7h">
              <h3 className="pb-2 text-sm font-semibold" data-id="element-966" data-mp="17cruxrqeecz9">Videos submitted</h3>
              {submissions.length === 0 ? <p className="text-sm text-muted" data-id="element-967" data-mp="1m99206rk15g0">None yet.</p> : <ul className="divide-y divide-line border-y border-line" data-id="element-968" data-mp="1hc8yo3m3agsl">
                  {submissions.map(v => <li key={v.day} className="flex gap-4 py-2.5 text-sm" data-id="element-969" data-mp="1v5qjdrqihzk7">
                      <span className="w-24 shrink-0 font-medium" data-id="element-970" data-mp="1r33thwdeiopn">Day {v.day}</span>
                      <span className="text-muted" data-id="element-971" data-mp="252lctw7xh9rm">{v.note || formatDay(v.day)}</span>
                    </li>)}
                </ul>}
            </div>}
        </section>

        <aside data-id="element-972" data-mp="2jpk15ibhno24">
          <h2 className="pb-2 text-lg font-semibold" data-id="element-973" data-mp="2jmxv5n48xgm0">{student ? "Group" : "Mentoring"}</h2>
          {groupIds.length === 0 ? <p className="text-sm text-muted" data-id="element-974" data-mp="29r499d4n0ark">Not assigned to a group yet.</p> : <ul className="space-y-3" data-id="element-975" data-mp="2epfl1tehsvcf">
              {groupIds.map(g => <li key={g} data-id="element-976" data-mp="2eril769d04uv">
                  <Link to={`/organizer/groups/${g}`} className="block rounded-xl border border-line bg-surface px-4 py-3 transition-colors duration-150 hover:bg-paper" data-id="element-977" data-mp="20xrc1yzjimzo">
                    <p className="font-semibold" data-id="element-978" data-mp="1r04ravvk817s">Group {g}</p>
                    <p className="text-sm text-muted" data-id="element-979" data-mp="1r1o7ynl4oea3">{subjectForGroup(g) ?? "Track not assigned"}</p>
                  </Link>
                </li>)}
            </ul>}
          {teammates.length > 0 && <>
              <h3 className="pb-2 pt-6 text-sm font-semibold" data-id="element-980" data-mp="1m10fryl5xrea">Teammates</h3>
              <ul className="space-y-2" data-id="element-981" data-mp="1m34iid7bskcz">
                {teammates.map(t => <li key={t.id} data-id="element-982" data-mp="3op05zfskiuex">
                    <Link to={`/organizer/people/${t.id}`} className="flex items-center gap-3 text-sm hover:underline" data-id="element-983" data-mp="3jwyys8a9btvr">
                      <Avatar name={t.name} school={t.school} photo={t.photo} size="sm" data-id="element-984" data-mp="3excgvljhhpgm" />
                      {t.name}
                    </Link>
                  </li>)}
              </ul>
            </>}
        </aside>
      </div>

    </main>;
}